/**
 * Expand the given range so it doesn't start or end in the middle of a word.
 * Word boundary = whitespace (space, newline, etc.) or start/end of container.
 * Used so contenteditable format (bold/italic/etc.) only wraps full words and matches display regex.
 * @param {HTMLElement} container - Contenteditable root
 * @param {Range} range - Current selection range
 * @returns {boolean} - true if range was modified
 */
function expandSelectionToWordBoundaries(container, range) {
  const fullText = collectText(container);
  if (!fullText.length) return false;

  const startOffset = charOffsetOf(container, range.startContainer, range.startOffset);
  const endOffset = charOffsetOf(container, range.endContainer, range.endOffset);

  let newStart = startOffset;
  let newEnd = endOffset;

  while (newStart > 0 && !/\s/.test(fullText[newStart - 1])) {
    newStart -= 1;
  }
  // Only expand end if it's not already at a word boundary.
  // When selecting a whole line, the browser may place the end at the start
  // of the next line (past <br> elements); the previous char is \n, so we're
  // already at a boundary and must not grab the next word.
  const endAlreadyAtBoundary = newEnd > 0 && /\s/.test(fullText[newEnd - 1]);
  if (!endAlreadyAtBoundary) {
    while (newEnd < fullText.length && !/\s/.test(fullText[newEnd])) {
      newEnd += 1;
    }
  }

  if (newStart === startOffset && newEnd === endOffset) return false;

  const startPos = nodeOffsetFromCharacterOffset(container, newStart);
  const endPos = nodeOffsetFromCharacterOffset(container, newEnd);
  if (!startPos || !endPos) return false;

  range.setStart(startPos.node, startPos.offset);
  range.setEnd(endPos.node, endPos.offset);
  return true;
}

function collectText(container) {
  let text = '';
  function visit(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else if (node.nodeName === 'BR') {
      text += '\n';
    } else {
      for (let c = node.firstChild; c; c = c.nextSibling) visit(c);
    }
  }
  visit(container);
  return text;
}

function charOffsetOf(container, targetNode, targetOffset) {
  let count = 0;
  let found = false;

  function visit(node) {
    if (found) return;
    if (node.nodeType === Node.TEXT_NODE) {
      if (node === targetNode) {
        count += targetOffset;
        found = true;
        return;
      }
      count += node.textContent.length;
    } else if (node.nodeName === 'BR') {
      count += 1;
    } else {
      for (let i = 0; i < node.childNodes.length; i += 1) {
        if (node === targetNode && i === targetOffset) {
          found = true;
          return;
        }
        visit(node.childNodes[i]);
        if (found) return;
      }
      if (node === targetNode && targetOffset === node.childNodes.length) {
        found = true;
      }
    }
  }

  visit(container);
  return count;
}

function nodeOffsetFromCharacterOffset(container, targetOffset) {
  let count = 0;

  function visit(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent.length;
      if (count + len >= targetOffset) {
        return { node, offset: targetOffset - count };
      }
      count += len;
    } else if (node.nodeName === 'BR') {
      if (count === targetOffset) {
        const parent = node.parentNode;
        const idx = Array.from(parent.childNodes).indexOf(node);
        return { node: parent, offset: idx };
      }
      count += 1;
    } else {
      for (let c = node.firstChild; c; c = c.nextSibling) {
        const result = visit(c);
        if (result) return result;
      }
    }
    return null;
  }

  const result = visit(container);
  if (result) return result;

  const walk = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
  let last;
  while (walk.nextNode()) last = walk.currentNode;
  return last ? { node: last, offset: last.textContent.length } : null;
}

export default expandSelectionToWordBoundaries;
